import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useCallback, useEffect, useRef, useState } from "react";
import { mergePdfs, PdfInfo } from "../api/postCall/mergePdfs";
import { ToastMessages } from "../enums/toastMessages";
import { logError } from "../utils/customLogger";

interface UseMergePdfsReturn {
  mergedFile: File | null;
  mergedPdfToRender: File | null;
  isMerging: boolean;
  mergeError: string | null;
  mergePdfsFromBase64: (
    base64Urls: string[],
    fileName?: string,
  ) => Promise<File | null>;
  mergePdfsFromInfo: (
    pdfInfo: PdfInfo[],
    fileName?: string,
  ) => Promise<File | null>;
  clearMergedFile: () => void;
}

/**
 * React hook that orchestrates a PDF-merge backend call and surfaces the
 * resulting `File` to the consumer.
 *
 * Behavior:
 *   - {@link mergePdfsFromBase64} accepts the `data:...;base64,...` URLs
 *     produced by {@link useFileToBase64}, strips the data-URL prefix,
 *     wraps each entry with the `file://` scheme the backend expects, then
 *     posts them to {@link mergePdfs}. On success the returned blob is
 *     wrapped into a `File` with the supplied `fileName` (defaults to
 *     `merged-document.pdf`).
 *   - {@link mergePdfsFromInfo} is a lower-level variant that accepts a
 *     pre-built {@link PdfInfo} list (useful when the source isn't raw
 *     base64 — e.g. URLs).
 *   - In-flight requests are cancelled via an internal `AbortController`
 *     whenever a new merge is started or the component unmounts, so stale
 *     responses can't overwrite fresh state.
 *   - Success/failure toasts use {@link ToastMessages.MERGE_PDFS_SUCCESS}
 *     and {@link ToastMessages.MERGE_PDFS_ERROR}; cancellation does NOT
 *     surface a toast (it's user-initiated).
 *   - {@link clearMergedFile} resets all state so the hook can be reused
 *     for a follow-up merge without stale data.
 */
export const useMergePdfs = (): UseMergePdfsReturn => {
  const [mergedFile, setMergedFile] = useState<File | null>(null);
  const [mergedPdfToRender, setMergedPdfToRender] = useState<File | null>(null);
  const [isMerging, setIsMerging] = useState<boolean>(false);
  const [mergeError, setMergeError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const formatBase64ForApi = useCallback((base64Url: string): string => {
    const base64Data = base64Url.includes(",")
      ? base64Url.split(",")[1]
      : base64Url;
    return `file://${base64Data}`;
  }, []);

  const blobToFile = useCallback((blob: Blob, fileName: string): File => {
    try {
      const file = new File([blob], fileName, { type: "application/pdf" });
      return file;
    } catch (error) {
      logError(error as Error, "blobToFile conversion error");
      throw error;
    }
  }, []);

  const mergePdfsFromBase64 = useCallback(
    async (
      base64Urls: string[],
      fileName: string = "merged-document.pdf",
    ): Promise<File | null> => {
      if (!base64Urls || base64Urls.length === 0) {
        setMergeError("No PDFs provided to merge");
        toast.error(ToastMessages.MERGE_PDFS_ERROR);
        return null;
      }

      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      abortControllerRef.current = new AbortController();
      const signal = abortControllerRef.current.signal;

      setIsMerging(true);
      setMergeError(null);

      try {
        const pdfInfo = base64Urls.map((base64Url, index) => ({
          source: formatBase64ForApi(base64Url),
          order: (index + 1).toString(),
        }));

        const response = await mergePdfs(pdfInfo, signal);

        if (!response || !response.blob) {
          throw new Error("Failed to merge PDFs - no blob in response");
        }

        let mergedPdfFile: File;
        try {
          mergedPdfFile = blobToFile(response.blob, fileName);
          setMergedFile(mergedPdfFile);
          setMergedPdfToRender(mergedPdfFile);
          toast.success(ToastMessages.MERGE_PDFS_SUCCESS);
        } catch (convertError) {
          logError(convertError as Error, "Error converting blob to file");
          throw new Error("Failed to convert merged PDF blob to file");
        }

        abortControllerRef.current = null;
        return mergedPdfFile;
      } catch (err) {
        if (err instanceof Error && err.name === "AbortError") {
          console.log("Merge PDFs request was cancelled");
        } else {
          const errorMessage = "Failed to merge PDFs. Please try again.";
          setMergeError(errorMessage);
          logError(err as Error, "useMergePdfs - mergePdfsFromBase64");
          toast.error(ToastMessages.MERGE_PDFS_ERROR);
        }
        abortControllerRef.current = null;
        return null;
      } finally {
        setIsMerging(false);
      }
    },
    [formatBase64ForApi, blobToFile],
  );

  const mergePdfsFromInfo = useCallback(
    async (
      pdfInfo: PdfInfo[],
      fileName: string = "merged-document.pdf",
    ): Promise<File | null> => {
      if (!pdfInfo || pdfInfo.length === 0) {
        setMergeError("No PDFs provided to merge");
        toast.error(ToastMessages.MERGE_PDFS_ERROR);
        return null;
      }

      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      abortControllerRef.current = new AbortController();
      const signal = abortControllerRef.current.signal;

      setIsMerging(true);
      setMergeError(null);

      try {
        const response = await mergePdfs(pdfInfo, signal);

        if (!response || !response.blob) {
          throw new Error("Failed to merge PDFs - no blob in response");
        }

        let mergedPdfFile: File;
        try {
          mergedPdfFile = blobToFile(response.blob, fileName);
          setMergedFile(mergedPdfFile);
          setMergedPdfToRender(mergedPdfFile);
          toast.success(ToastMessages.MERGE_PDFS_SUCCESS);
        } catch (convertError) {
          logError(convertError as Error, "Error converting blob to file");
          throw new Error("Failed to convert merged PDF blob to file");
        }

        abortControllerRef.current = null;
        return mergedPdfFile;
      } catch (err) {
        if (err instanceof Error && err.name === "AbortError") {
          console.log("Merge PDFs request was cancelled");
        } else {
          const errorMessage = "Failed to merge PDFs. Please try again.";
          setMergeError(errorMessage);
          logError(err as Error, "useMergePdfs - mergePdfsFromInfo");
          toast.error(ToastMessages.MERGE_PDFS_ERROR);
        }
        abortControllerRef.current = null;
        return null;
      } finally {
        setIsMerging(false);
      }
    },
    [blobToFile],
  );

  const clearMergedFile = useCallback(() => {
    setMergedFile(null);
    setMergedPdfToRender(null);
    setMergeError(null);
  }, []);

  return {
    mergedFile,
    mergedPdfToRender,
    isMerging,
    mergeError,
    mergePdfsFromBase64,
    mergePdfsFromInfo,
    clearMergedFile,
  };
};
